import { useEffect, useRef } from "react";
import type { RefObject } from "react";

// See: https://reactnative.dev/docs/appstate#app-states
import { AppState } from "react-native";
// possible values: active, inactive, background, unknown, extension
import type { AppStateStatus } from "react-native";

interface StateChange {
  currentState: AppStateStatus;
  nextState: AppStateStatus;
}

interface UseAppForegroundStateArgs {
  onChangeRef: RefObject<({ currentState, nextState }: StateChange) => void>;
}

// A simple hook that takes a change callback Ref to be invoked every time AppForegroundState changes
const useAppForegroundState = ({ onChangeRef }: UseAppForegroundStateArgs) => {
  // Refs are used to minimize unnecessary AppState.addEventListener/removeEventListener calls
  // & corresponding re-renders, for what is effectively the same callback functionally,
  // and to prevent short-lived race conditions where AppState transitions may occur between add/remove calls
  const currentAppForegroundStateRef = useRef(AppState.currentState);

  useEffect(() => {
    const handleAppForegroundStateChange = (
      nextAppForegroundState: AppStateStatus,
    ) => {
      // call the passed in change callback/handler
      onChangeRef.current({
        currentState: currentAppForegroundStateRef.current,
        nextState: nextAppForegroundState,
      });

      // update the AppForegroundState ref to reflect the newest state
      currentAppForegroundStateRef.current = nextAppForegroundState;
    };

    // Register the eventListener to changes of AppState;
    // to be unmounted/re-registered only if any useEffect dependencies change
    const subscription = AppState.addEventListener(
      "change",
      handleAppForegroundStateChange,
    );
    return () => {
      subscription.remove();
    };
  }, [currentAppForegroundStateRef, onChangeRef]);
};

export default useAppForegroundState;
export type { StateChange };
